import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeDefaultInformation } from "../../Store/TemplateStore/template.action";
import {
  editDefaultCIPValues,
  getDefaultCIPValues,
} from "../../network/cipAPI";
import { useToastNotification } from "../../packages/WPToastNotification/WPToastNotificationProvider";
import { useTranslation } from "react-i18next";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import { SaveIcon } from "../../packages/SVGs";
import CircularLoader from "../../common/styles/components/circularLoader";
import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";
import CIPSection from "./CIPSection";
import { validationRangeHardError } from "../../packages/WPInput/WPRangedInput";
import { INITIAL_DEFAULT_CIP_ERROR_STATE } from "./defaultCIPInformationFields.config";
import {
  STAGE_DEFAULTS,
  DEFAULT_CIP_AMOUNT_PER_ELEMENT,
  DEFAULT_CIP_API_KEY_MAP,
} from "../../constants/global.constants";
import { getLocalStorageItem } from "../../utils/session";
import { isNullOrUndefined } from "../../utils/appUtils";

import "./index.scss";

const normalizeDefaultCIPPayloadForApi = (payload = {}) => {
  return Object.keys(payload).reduce((acc, key) => {
    const apiKey = DEFAULT_CIP_API_KEY_MAP[key] || key;
    acc[apiKey] = payload[key];
    return acc;
  }, {});
};

const DefaultCIPInformation = React.forwardRef(
  ({
    selectedTrain,
    defaultCIPKeys,
    disabled,
    setMandatoryComponentsStatus,
    noOfRONFElements,
    selectedPass,
    selectedStage,
  }) => {
    const { showSuccessNotification, showErrorNotification } =
      useToastNotification();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { temperature, cips, defaultCIPValues, plantChemicalLibrary } =
      useSelector((state) => ({
        temperature: state.temperature,
        cips: state.cips,
        plantChemicalLibrary: state.plantChemicalLibrary,
        defaultCIPValues: {
          acid_price: state.acid_price,
          alkali_price: state.alkali_price,
          additional_chemical_price: state.additional_chemical_price,
          acid_chem_id: state.acid_chem_id,
          alkali_chem_id: state.alkali_chem_id,
          additional_chemical_chem_id: state.additional_chemical_chem_id,
          acid_enabled: state.acid_enabled,
          alkali_enabled: state.alkali_enabled,
          additional_chemical_enabled: state.additional_chemical_enabled,
          acid_used_per_cip: state.acid_used_per_cip,
          alkali_used_per_cip: state.alkali_used_per_cip,
          additional_chemical_per_cip: state.additional_chemical_per_cip,
          acid_cip_ph: state.acid_cip_ph,
          alkali_cip_ph: state.alkali_cip_ph,
          cip_solution_ph: state.cip_solution_ph,
          acid_cip_temp: state.acid_cip_temp,
          alkali_cip_temp: state.alkali_cip_temp,
          cip_solution_temp: state.cip_solution_temp,
          acid_cip_duration: state.acid_cip_duration,
          alkali_cip_duration: state.alkali_cip_duration,
          cip_solution_duration: state.cip_solution_duration,
        },
      }));
    const [changedCIPValues, setChangedCIPValues] = useState({});
    const [errors, setErrors] = useState(INITIAL_DEFAULT_CIP_ERROR_STATE);
    const [hasMissingRequiredFields, setHasMissingRequiredFields] =
      useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const changedCIPValuesRef = useRef(changedCIPValues);
    const defaultCIPValuesRef = useRef(defaultCIPValues);
    const hasDefaultCIPInitialMount = useRef(false);

    const noError = useMemo(
      () => Object.values(errors).every((error) => error === null),
      [errors],
    );

    const getFieldValidationError = useCallback(
      (value, minValue, maxValue) => {
        if (isNullOrUndefined(minValue) || isNullOrUndefined(maxValue)) {
          return null;
        }

        const normalizedValue =
          value === null || value === undefined ? "" : String(value).trim();

        if (normalizedValue === "") {
          return t("ValueStrings.requiredField");
        }

        const numericValue = Number(normalizedValue);
        if (Number.isNaN(numericValue)) {
          return t("ValueStrings.requiredField");
        }

        const hardError = validationRangeHardError(t, numericValue, [
          minValue,
          maxValue,
        ]);

        return hardError?.error || null;
      },
      [t],
    );

    const handleInputChange = (field, value, minValue, maxValue) => {
      setChangedCIPValues((prev) => ({ ...prev, [field]: value }));

      setErrors((prev) => ({
        ...prev,
        [field]: getFieldValidationError(value, minValue, maxValue),
      }));
    };

    const handleSectionValidationToggle = useCallback(
      (_category, isEnabled, sectionFields = []) => {
        setErrors((prev) => {
          const nextErrors = { ...prev };

          sectionFields.forEach((field) => {
            const key = field?.key;
            if (!key) return;

            if (!isEnabled) {
              nextErrors[key] = null;
              return;
            }

            const changedValue = changedCIPValuesRef.current?.[key];
            const defaultValue = defaultCIPValuesRef.current?.[key];
            const value =
              changedValue !== undefined ? changedValue : defaultValue;
            const minValue = field?.ranges?.hard?.min ?? null;
            const maxValue = field?.ranges?.hard?.max ?? null;

            nextErrors[key] = getFieldValidationError(
              value,
              minValue,
              maxValue,
            );
          });

          return nextErrors;
        });
      },
      [getFieldValidationError],
    );

    const handleSectionFieldChange = (field, value) => {
      setChangedCIPValues((prev) => ({ ...prev, [field]: value }));
    };

    // Helper to fetch and restore last saved values
    const refreshDefaultCIPFromServer = async () => {
      try {
        const res = await getDefaultCIPValues(selectedTrain);
        const defaultInfo = res?.data;
        if (defaultInfo) {
          dispatch(storeDefaultInformation(defaultInfo));
        }
      } catch (getError) {
        showErrorNotification(t("defaultInformationStrings.fetchFailed"));
      }
    };

    const getSaveButtonState = () => {
      const hasChanges = Object.keys(changedCIPValues).length > 0;
      return !(hasChanges && noError && !hasMissingRequiredFields);
    };

    const handleSave = async (newChangedValues = {}) => {
      const hasAutoSaveValues = Object.keys(newChangedValues).length > 0;
      const changedValuesToSave = hasAutoSaveValues
        ? { ...changedCIPValues, ...newChangedValues }
        : changedCIPValues;

      const payload = normalizeDefaultCIPPayloadForApi(changedValuesToSave);

      if (Object.keys(payload).length === 0) {
        return;
      }

      setIsSaving(true);
      try {
        const editRes = await editDefaultCIPValues(selectedTrain, payload);
        if (editRes?.detail || Object.keys(editRes).length === 0) {
          showErrorNotification(t("defaultInformationStrings.saveFailed"));
          await refreshDefaultCIPFromServer();
          return;
        }
        // Only update store if save is successful
        dispatch(storeDefaultInformation(editRes));

        // don't show success notification in case auto-save
        if (Object.keys(newChangedValues).length === 0) {
          showSuccessNotification(t("defaultInformationStrings.saveSuccess"));
        }
      } catch (editError) {
        showErrorNotification(t("defaultInformationStrings.saveFailed"));
        await refreshDefaultCIPFromServer();
      } finally {
        setChangedCIPValues({});
        setIsSaving(false);
      }
    };

    useEffect(() => {
      changedCIPValuesRef.current = changedCIPValues;
    }, [changedCIPValues]);

    useEffect(() => {
      defaultCIPValuesRef.current = defaultCIPValues;
    }, [defaultCIPValues]);

    useEffect(() => {
      if (
        !Array.isArray(plantChemicalLibrary) ||
        plantChemicalLibrary.length === 0
      ) {
        return;
      }

      if (!hasDefaultCIPInitialMount.current) {
        hasDefaultCIPInitialMount.current = true;
        return;
      }

      refreshDefaultCIPFromServer();
    }, [plantChemicalLibrary]);

    useEffect(() => {
      if (cips === null) {
        setErrors((prev) => ({
          ...prev,
          acid_cip_temp: null,
          alkali_cip_temp: null,
          cip_solution_temp: null,
        }));
      }
    }, [temperature, cips]);

    useEffect(() => {
      setChangedCIPValues({});
      setErrors(INITIAL_DEFAULT_CIP_ERROR_STATE);
    }, [selectedTrain]);

    useEffect(() => {
      if (setMandatoryComponentsStatus == null) return;
      const hasChanges = Object.keys(changedCIPValues).length > 0;
      const defaultCIPInfoState = {
        fulfilled: !hasChanges && noError && !hasMissingRequiredFields,
        changed: hasChanges,
        error: !noError || hasMissingRequiredFields,
      };
      setMandatoryComponentsStatus((prev) => {
        return { ...prev, defaultCIPInfo: defaultCIPInfoState };
      });
    }, [changedCIPValues, noError, hasMissingRequiredFields]);

    const initialLoadRef = useRef(true);
    const prevNoOfRONFElementsRef = useRef(noOfRONFElements);

    useEffect(() => {
      if (isNullOrUndefined(noOfRONFElements)) return;
      if (initialLoadRef.current) {
        initialLoadRef.current = false;

        const prevTrainConfig = JSON.parse(
          getLocalStorageItem("prevTrainConfig"),
        );

        if (!prevTrainConfig) {
          const { NUM_PRESSURE_VESSELS, NUM_ELEMENTS_PER_PV } = STAGE_DEFAULTS;
          prevNoOfRONFElementsRef.current =
            NUM_PRESSURE_VESSELS * NUM_ELEMENTS_PER_PV;
        } else {
          if (selectedStage === "All") {
            let totalElements = 0;

            Object.values(
              prevTrainConfig[selectedTrain]?.[selectedPass],
            ).forEach((stageElements) => {
              totalElements += stageElements;
            });
            prevNoOfRONFElementsRef.current = totalElements;
          } else {
            prevNoOfRONFElementsRef.current =
              prevTrainConfig[selectedTrain]?.[selectedPass]?.[selectedStage];
          }
        }
      }

      const { NUM_PRESSURE_VESSELS, NUM_ELEMENTS_PER_PV } = STAGE_DEFAULTS;
      const { ACID, ALKALI } = DEFAULT_CIP_AMOUNT_PER_ELEMENT;
      const defaultNoOfElements = NUM_PRESSURE_VESSELS * NUM_ELEMENTS_PER_PV;

      const currNoOfElements = Number(noOfRONFElements) || defaultNoOfElements;

      const currAcid = (ACID * currNoOfElements).toFixed(2);
      const currAlkali = (ALKALI * currNoOfElements).toFixed(2);

      // Always update acid and alkali values when number of elements changes
      const newChangedCIPValues = {
        acid_used_per_cip: currAcid,
        alkali_used_per_cip: currAlkali,
      };
      handleSave(newChangedCIPValues);
      prevNoOfRONFElementsRef.current = noOfRONFElements;
    }, [noOfRONFElements]);

    return (
      <div className="ro-card-wrapper default-cip-information-card">
        {isSaving && (
          <div className="circular-loader-overlay">
            <CircularLoader />
          </div>
        )}
        <div>
          <div className="ro-card-header">
            <div className=" info-section">
              <div className="teal-heading">
                {t("defaultInformationStrings.defaultInformation")}
              </div>
              {!disabled && (
                <IconWithTooltip
                  label={t("helpInformation.defaultCIPInformationHelp")}
                />
              )}
            </div>
            <WPTertiaryButton
              variant="outlined"
              label={t("defaultInformationStrings.save")}
              onClick={() => handleSave()}
              icon={<SaveIcon width="16" height="16" />}
              iconPlacement="before"
              size="small"
              disabled={getSaveButtonState() || disabled}
            />
          </div>
          <CIPSection
            defaultCIPKeys={defaultCIPKeys}
            errors={errors}
            handleInputChange={handleInputChange}
            onFieldChange={handleSectionFieldChange}
            onSectionValidationToggle={handleSectionValidationToggle}
            onMandatoryValidationChange={setHasMissingRequiredFields}
            disabled={disabled}
            existingCIPState={changedCIPValues}
          />
        </div>
      </div>
    );
  },
);

export default DefaultCIPInformation;
